import React, { useState, useEffect, useContext } from 'react';
import { Card, Button, Table, Form, InputGroup } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import PaginationControls from '../common/PaginationControls';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: parseInt(localStorage.getItem('preferredPageSize') || '25'),
    total: 0,
    totalPages: 0
  });
  const [sortBy, setSortBy] = useState('CreatedAt');
  const [sortDir, setSortDir] = useState('DESC');
  
  const { token } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    // Parse query parameters
    const params = new URLSearchParams(location.search);
    const pageParam = parseInt(params.get('page')) || 1;
    const searchParam = params.get('search') || '';
    const statusParam = params.get('status') || 'All';
    const sortByParam = params.get('sortBy') || 'CreatedAt';
    const sortDirParam = params.get('sortDir') || 'DESC';
    
    // Update state from URL params
    setPagination(prev => ({ ...prev, page: pageParam }));
    setSearchTerm(searchParam);
    setStatusFilter(statusParam);
    setSortBy(sortByParam);
    setSortDir(sortDirParam);
    
    // Fetch projects with pagination
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        
        // Build query string with pagination and filter params
        const queryParams = new URLSearchParams({
          page: pageParam.toString(),
          pageSize: pagination.pageSize.toString(),
          sortBy: `p.${sortByParam}`,
          sortDir: sortDirParam
        });
        
        // If search filters are applied, use noPagination to filter on client side
        const searchFilterActive = searchParam || statusParam !== 'All';
        if (searchFilterActive) {
          queryParams.set('noPagination', 'true');
        }
        
        const response = await fetch(`/api/projects?${queryParams.toString()}`, {
          headers: {
            'x-auth-token': token
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch projects');
        }
        
        const data = await response.json();
        
        if (searchFilterActive) {
          // Handle client-side filtering when search/status filters are active
          setProjects(data);
          // Filtering will happen in the filter effect below
        } else {
          // Use server-side pagination when no search filters
          setProjects(data.data);
          setFilteredProjects(data.data);
          setPagination(data.pagination);
        }
      } catch (err) {
        console.error('Error fetching projects:', err);
        toast.error('Failed to load projects');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchProjects();
  }, [token, location.search, pagination.pageSize]);
  
  useEffect(() => {
    // Filter projects based on search term and status
    let filtered = projects;
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        project => 
          project.ProjectName.toLowerCase().includes(term) ||
          project.ClientName.toLowerCase().includes(term) ||
          (project.Description && project.Description.toLowerCase().includes(term))
      );
    }
    
    if (statusFilter !== 'All') {
      filtered = filtered.filter(project => project.Status === statusFilter);
    }
    
    setFilteredProjects(filtered);
  }, [searchTerm, statusFilter, projects]);
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    updateUrlParams({ search: e.target.value, page: 1 });
  };
  
  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    updateUrlParams({ status: e.target.value, page: 1 });
  };
  
  const handlePageChange = (newPage) => {
    updateUrlParams({ page: newPage });
  };
  
  const handleSortChange = (column) => {
    // Toggle sort direction if clicking the same column
    const newSortDir = column === sortBy && sortDir === 'ASC' ? 'DESC' : 'ASC';
    setSortBy(column);
    setSortDir(newSortDir);
    updateUrlParams({ sortBy: column, sortDir: newSortDir });
  };
  
  // Update URL parameters without full page reload
  const updateUrlParams = (newParams) => {
    const searchParams = new URLSearchParams(location.search);
    
    // Update provided parameters
    Object.entries(newParams).forEach(([key, value]) => {
      if (value === null || value === '' || (key === 'status' && value === 'All')) {
        searchParams.delete(key);
      } else {
        searchParams.set(key, value);
      }
    });
    
    // Navigate to the new URL
    navigate({ search: searchParams.toString() });
  };
  
  if (isLoading) {
    return <div className="text-center mt-5">Loading projects...</div>;
  }
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Projects</h2>
        <Button as={Link} to="/projects/create" variant="primary">
          Create Project
        </Button>
      </div>
      
      <Card className="mb-4">
        <Card.Body>
          <div className="row">
            <div className="col-md-8 mb-3 mb-md-0">
              <InputGroup>
                <Form.Control
                  placeholder="Search projects by name, client or description..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </InputGroup>
            </div>
            <div className="col-md-4">
              <Form.Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
              >
                <option value="All">All Statuses</option>
                <option value="New">New</option>
                <option value="Proposal Sent">Proposal Sent</option>
                <option value="Payment Schedule Sent">Payment Schedule Sent</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="On Hold">On Hold</option>
              </Form.Select>
            </div>
          </div>
        </Card.Body>
      </Card>
      
      <Card>
        <Card.Body>
          {filteredProjects.length > 0 ? (
            <div className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    <th onClick={() => handleSortChange('ProjectName')} style={{ cursor: 'pointer' }}>
                      Project Name {sortBy === 'ProjectName' && (sortDir === 'ASC' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSortChange('ClientName')} style={{ cursor: 'pointer' }}>
                      Client {sortBy === 'ClientName' && (sortDir === 'ASC' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSortChange('Status')} style={{ cursor: 'pointer' }}>
                      Status {sortBy === 'Status' && (sortDir === 'ASC' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSortChange('StartDate')} style={{ cursor: 'pointer' }}>
                      Start Date {sortBy === 'StartDate' && (sortDir === 'ASC' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSortChange('EndDate')} style={{ cursor: 'pointer' }}>
                      End Date {sortBy === 'EndDate' && (sortDir === 'ASC' ? '↑' : '↓')}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProjects.map(project => (
                    <tr key={project.ProjectId}>
                      <td>{project.ProjectName}</td>
                      <td>{project.ClientName}</td>
                      <td>
                        <span 
                          style={{
                            backgroundColor: 
                              project.Status === 'New' ? '#007bff' :
                              project.Status === 'Proposal Sent' ? '#fd7e14' :
                              project.Status === 'Payment Schedule Sent' ? '#8B5CF6' :
                              project.Status === 'In Progress' ? '#6c757d' :
                              project.Status === 'Completed' ? '#28a745' :
                              project.Status === 'On Hold' ? '#dc3545' :
                              '#6c757d',
                            color: project.Status === 'Payment Schedule Sent' ? '#fff' : '#fff',
                            padding: '0.375rem 0.75rem',
                            borderRadius: '0.375rem',
                            fontSize: '0.875rem',
                            fontWeight: '500',
                            display: 'inline-block',
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            border: 'none'
                          }}
                        >
                          {project.Status}
                        </span>
                      </td>
                      <td>
                        {project.StartDate 
                          ? new Date(project.StartDate).toLocaleDateString() 
                          : 'Not set'}
                      </td>
                      <td>
                        {project.EndDate 
                          ? new Date(project.EndDate).toLocaleDateString() 
                          : 'Not set'}
                      </td>
                      <td>
                        <Button
                          as={Link}
                          to={`/projects/${project.ProjectId}`}
                          variant="primary"
                          size="sm"
                          className="me-2"
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <p className="text-center">
              {projects.length > 0 
                ? 'No projects match your search criteria.' 
                : 'No projects found. Create your first project.'}
            </p>
          )}
        </Card.Body>
        {/* Only show pagination when search filters are not active */}
        {(searchTerm === '' && statusFilter === 'All' && pagination.totalPages > 1) && (
          <Card.Footer>
            <PaginationControls
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
            />
          </Card.Footer>
        )}
      </Card>
    </div>
  );
};

export default Projects;